import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getSubscriptionStatus, completeSubscription } from '../services/onboardingService';
import { paymentService } from '../services/paymentService';
import PaymentModal from './PaymentModal';
import styles from '../styles/OnboardingFlow.module.css';
import DataFallback from './feedback/DataFallback';
// Onboarding plan cards keep their own copy of the card styles so they are
// unaffected by the redesigned (comparison-grid) PricingPage.module.css.
import pricingStyles from '../styles/SubscriptionPlans.module.css';

export default function SubscriptionSelection({ onSubscriptionComplete }) {
  // Static icon placeholders – drop your PNGs in public/icons/pricing/
  const ICONS = {
    check: process.env.PUBLIC_URL + '/icons/pricing/check.png',
    cross: process.env.PUBLIC_URL + '/icons/pricing/cross.png',
    flower: process.env.PUBLIC_URL + '/icons/pricing/flower.png',
  };

  const renderFeatureText = (input) => {
    const tokens = /\[bold\]|\[#([0-9a-fA-F]{3,6})\]/g;
    const nodes = [];
    let bold = false;
    let color = null;
    let lastIndex = 0;
    let match;
    const pushText = (text) => {
      if (!text) return;
      nodes.push(
        <span
          key={`t-${nodes.length}`}
          className={bold ? pricingStyles.featureBold : undefined}
          style={color ? { color: `#${color}` } : undefined}
        >
          {text}
        </span>
      );
    };
    while ((match = tokens.exec(input)) !== null) {
      const before = input.slice(lastIndex, match.index);
      pushText(before);
      if (match[0].toLowerCase() === '[bold]') {
        bold = !bold;
      } else {
        const hex = match[1];
        color = color === hex ? null : hex;
      }
      lastIndex = match.index + match[0].length;
    }
    pushText(input.slice(lastIndex));
    return nodes;
  };
  const token = useSelector(state => state.user.token);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [userBrandId, setUserBrandId] = useState(null);

  // Get user's brand ID
  useEffect(() => {
    const fetchUserBrandId = async () => {
      if (!token) return;
      
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('User profile response:', data); // Debug log
          
          // NEW API FORMAT: data.user.brand.id
          let brandId = null;
          if (data && data.user && data.user.brand) {
            brandId = data.user.brand.id;
          }
          
          if (brandId) {
            console.log('Found brand ID:', brandId);
            setUserBrandId(brandId);
          } else {
            console.warn('No brand ID found in user data:', data);
            setError('Unable to get brand information. Please refresh and try again.');
          }
        } else {
          console.error('Failed to fetch user profile:', response.status, response.statusText);
          setError('Unable to get brand information. Please refresh and try again.');
        }
      } catch (err) {
        console.error('Error fetching user brand ID:', err);
        setError('Unable to get brand information. Please refresh and try again.');
      }
    };

    fetchUserBrandId();
  }, [token]);

  // Fetch subscription status
  useEffect(() => {
    if (token) {
      getSubscriptionStatus(token)
        .then(data => {
          setSubscriptionStatus(data);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [token]);

  // Check for payment completion when component mounts/updates
  useEffect(() => {
    const checkPaymentCompletion = async () => {
      const paymentContext = localStorage.getItem('payment_context');
      const pendingTransactionId = localStorage.getItem('pending_transaction_id');
      
      if (paymentContext === 'onboarding' && pendingTransactionId && token) {
        console.log('SubscriptionSelection: Detected returning from onboarding payment, checking status');
        
        try {
          // Check if payment was successful
          const paymentStatus = await paymentService.getPaymentStatus(pendingTransactionId, token);
          
          if (paymentStatus.status === 'success') {
            console.log('SubscriptionSelection: Payment successful, refreshing subscription status');
            
            // Refresh subscription status
            const updatedStatus = await getSubscriptionStatus(token);
            setSubscriptionStatus(updatedStatus);
            
            // Clear payment context and pending transaction
            localStorage.removeItem('payment_context');
            localStorage.removeItem('pending_transaction_id');
            localStorage.removeItem('pending_plan_id');
            
            // If user now has subscription, trigger onboarding completion
            if (updatedStatus.has_subscription && onSubscriptionComplete) {
              console.log('SubscriptionSelection: Triggering onboarding completion after payment');
              onSubscriptionComplete();
            }
          }
        } catch (err) {
          console.error('Error checking payment completion:', err);
        }
      }
    };

    checkPaymentCompletion();
  }, [token, onSubscriptionComplete]);

  const handleChoosePlan = (plan) => {
    console.log('handleChoosePlan called:', { plan, userBrandId }); // Debug log
    
    if (!userBrandId) {
      console.error('No userBrandId available for payment modal');
      setError('Unable to get brand information. Please refresh and try again.');
      return;
    }
    
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handleCloseModal = () => {
    setShowPaymentModal(false);
    setSelectedPlan(null);
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    setSelectedPlan(null);
    
    // Refresh subscription status after payment
    try {
      const updatedStatus = await getSubscriptionStatus(token);
      setSubscriptionStatus(updatedStatus);
    } catch (err) {
      console.error('Error refreshing subscription status:', err);
    }
  };

  const handleCompleteOnboarding = async () => {
    if (!subscriptionStatus?.has_subscription) {
      setError('Please select and pay for a subscription plan first.');
      return;
    }

    setCompleting(true);
    setError(null);

    try {
      await completeSubscription(token);
      
      // Trigger navigation to welcome screen
      if (onSubscriptionComplete) {
        onSubscriptionComplete();
      }
    } catch (err) {
      setError('Failed to complete onboarding: ' + err.message);
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading subscription options...</div>;
  }

  if (error) {
    // This sits between onboarding and payment, so a failure here stops someone
    // subscribing. "Error:" plus a raw message read as "your account is broken"
    // when the usual cause is a momentary outage.
    return (
      <DataFallback
        variant="pane"
        error={typeof error === 'string' ? { message: error, retryable: true } : error}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <div className={styles.subscriptionContainer}>
      <div className={styles.subscriptionHeader}>
        <h2>Choose Your Subscription Plan</h2>
        <p>Your onboarding form has been submitted successfully. Please select a subscription plan to continue.</p>
        {subscriptionStatus?.has_subscription && (
          <div className={styles.currentSubscription}>
            <h3>✓ Current Subscription</h3>
            <div className={styles.subscriptionInfo}>
              <strong>{subscriptionStatus.current_subscription.plan.name}</strong>
              <span>₹{subscriptionStatus.current_subscription.plan.price_per_month}/month</span>
              <span>Active until: {new Date(subscriptionStatus.current_subscription.end_date).toLocaleDateString()}</span>
            </div>
          </div>
        )}
      </div>

      {/* Display Available Plans */}
      {subscriptionStatus?.available_plans && (
        <div className={pricingStyles.plansContainer}>
          {subscriptionStatus.available_plans.map((plan) => {
            const isCurrent = subscriptionStatus.has_subscription && subscriptionStatus.current_subscription.plan.id === plan.id;
            return (
              <div
                key={plan.id}
                className={`${pricingStyles.planCard} ${isCurrent ? pricingStyles.currentPlan : ''}`}
              >
                {isCurrent && (
                  <div className={pricingStyles.currentPlanBadge}>Current Plan</div>
                )}
                <h2 className={pricingStyles.planName}>{plan.name}</h2>
                <div className={pricingStyles.planPrice}>{(Number(plan.price_per_month) || 0) ? `₹${plan.price_per_month}/mo` : 'Free'}</div>
                <div style={{ marginBottom: '0.7rem', color: '#555', fontSize: '1rem' }}>{plan.description}</div>
                <ul className={pricingStyles.featuresList}>
                  {plan.features && plan.features.map((feature) => (
                    <li key={feature.id} className={pricingStyles.featureItem}>
                      {(() => {
                        const raw = (feature.name || '').trim();
                        const iconMatch = raw.match(/^\[icon:(check|cross|flower)\]\s*/i);
                        const prefixed = raw.replace(/^([✓✗])\s*/, (_, s) => (s === '✓' ? '[icon:check] ' : '[icon:cross] '));
                        const text = (iconMatch ? raw.replace(iconMatch[0], '') : prefixed.replace(/^\[icon:(check|cross|flower)\]\s*/i, '')).trim();
                        const iconKey = (iconMatch ? iconMatch[1] : (raw.startsWith('✓') ? 'check' : raw.startsWith('✗') ? 'cross' : 'flower')).toLowerCase();
                        const iconSrc = ICONS[iconKey] || ICONS.flower;
                        const isIncluded = iconKey === 'check';
                        const isExcluded = iconKey === 'cross';
                        return (
                          <>
                            <span className={`${isIncluded ? pricingStyles.featureIncluded : ''} ${isExcluded ? pricingStyles.featureNotIncluded : ''} ${pricingStyles.featureIcon}`}>
                              <img src={iconSrc} alt={iconKey} style={{ width: 14, height: 14 }} />
                            </span>
                            <span className={pricingStyles.featureName}>{renderFeatureText(text)}</span>
                          </>
                        );
                      })()}
                      {feature.description && (
                        <div className={pricingStyles.infoIconContainer}>
                          <span className={pricingStyles.infoIcon}>ℹ️</span>
                          <div className={pricingStyles.tooltip}>{feature.description}</div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <button
                  className={`${pricingStyles.ctaButton} ${isCurrent ? pricingStyles.currentPlanButton : ''}`}
                  disabled={isCurrent}
                  onClick={() => handleChoosePlan(plan)}
                >
                  {isCurrent ? 'Current Plan' : 'Choose Plan'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Complete Onboarding Button - only show if user has subscription */}
      {subscriptionStatus?.has_subscription && (
        <div className={styles.completeSection}>
          <button
            className={styles.completeButton}
            onClick={handleCompleteOnboarding}
            disabled={completing}
          >
            {completing ? 'Completing Onboarding...' : 'Complete Onboarding'}
          </button>
          <p className={styles.completeNote}>
            Your subscription is active. Click to finish the onboarding process and access your dashboard.
          </p>
        </div>
      )}

      {!subscriptionStatus?.has_subscription && subscriptionStatus?.available_plans && (
        <div className={styles.noSubscriptionNote}>
          <p>Please select and complete payment for a subscription plan above to continue.</p>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedPlan && userBrandId && (
        <PaymentModal
          plan={selectedPlan}
          brandId={userBrandId}
          onClose={handleCloseModal}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
}
